import { useCallback, useEffect, useState } from "react";
import { requestApi } from "../core/api/api";
import { getImageUri } from "../core/utils/imageLoad";
import type {
	AddressesResponse,
	ApiAddress,
	UserResponse,
} from "../core/types/tsx/pages/UserProfile.types";
import type { UserOrder } from "../core/types/tsx/components/UserDetails.types";
import type {
	ApiOrderRaw,
	ApiRequestRaw,
	UseUserProfileDataResult,
	UserProfileView,
} from "../core/types/tsx/pages/UserProfile.page.types";

type SessionLike = {
	token?: string | null;
	user?: {
		id?: number;
		type_user?: string | null;
	};
};

function resolveUserFromPayload(payload: UserResponse): UserResponse["user"] | null {
	if (payload?.user) return payload.user;
	const flat = payload as unknown as UserResponse["user"];
	if (flat && typeof flat === "object" && typeof flat.id === "number") return flat;
	return null;
}

function extractAddresses(payload: unknown): ApiAddress[] {
	if (Array.isArray(payload)) return payload as ApiAddress[];
	if (!payload || typeof payload !== "object") return [];
	const record = payload as Record<string, unknown>;
	const candidates = [record.addresses, record.address, record.items, record.data];
	for (const candidate of candidates) {
		if (Array.isArray(candidate)) return candidate as ApiAddress[];
	}
	return [];
}

function extractAddressesFromUserPayload(payload: unknown): ApiAddress[] {
	if (!payload || typeof payload !== "object") return [];
	const record = payload as Record<string, unknown>;

	if (Array.isArray(record.addresses)) {
		return record.addresses as ApiAddress[];
	}

	if (record.user && typeof record.user === "object") {
		const nested = record.user as Record<string, unknown>;
		if (Array.isArray(nested.addresses)) {
			return nested.addresses as ApiAddress[];
		}
	}

	return [];
}

function extractClientIdFromRelation(
	relation: unknown,
): number | null {
	const parsePositiveId = (value: unknown): number | null => {
		if (typeof value === "number" && Number.isFinite(value) && value > 0) {
			return value;
		}

		if (typeof value === "string") {
			const parsed = Number(value);
			if (Number.isFinite(parsed) && parsed > 0) return parsed;
		}

		return null;
	};

	if (Array.isArray(relation)) {
		for (const item of relation) {
			const parsed = parsePositiveId((item as { id?: unknown })?.id);
			if (parsed) return parsed;
		}
		return null;
	}

	if (relation && typeof relation === "object") {
		return parsePositiveId((relation as { id?: unknown }).id);
	}

	return null;
}

export function useUserProfileData(
	session: SessionLike | null,
): UseUserProfileDataResult {
	const fetchWithApi = useCallback(async <T,>(endpoint: string) => {
		return (await requestApi<T>("GET", endpoint)) as T;
	}, []);

	const [profile, setProfile] = useState<UserProfileView | null>(null);
	const [addresses, setAddresses] = useState<ApiAddress[]>([]);
	const [orders, setOrders] = useState<UserOrder[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const refreshAddresses = useCallback(async () => {
		if (!session?.user?.id) return;
		try {
			const userPayload = await fetchWithApi<UserResponse>(
				`/users/${session.user.id}`,
			).catch(() => null);
			const addressesFromUser = extractAddressesFromUserPayload(userPayload);
			if (addressesFromUser.length > 0) {
				setAddresses(addressesFromUser);
				return;
			}

			const addressPayload = await fetchWithApi<
				AddressesResponse | ApiAddress[]
			>(`/addresses/client/${session.user.id}`).catch(
				() => ({ addresses: [] }) as AddressesResponse,
			);
			setAddresses(extractAddresses(addressPayload));
		} catch {
			setAddresses([]);
		}
	}, [fetchWithApi, session?.user?.id]);

	useEffect(() => {
		let cancelled = false;

		const formatDate = (value?: string | null) => {
			if (!value) return "-";
			const date = new Date(value);
			return Number.isNaN(date.getTime())
				? value
				: new Intl.DateTimeFormat("es-VE", {
						day: "2-digit",
						month: "long",
						year: "numeric",
					}).format(date);
		};

		// const formatDateLong = (value?: string | null) => {
		// 	if (!value) return "-";
		// 	const date = new Date(value);
		// 	return Number.isNaN(date.getTime())
		// 		? value
		// 		: new Intl.DateTimeFormat("es-VE", {
		// 				weekday: "long",
		// 				day: "numeric",
		// 				month: "long",
		// 				year: "numeric",
		// 			}).format(date);
		// };

		const formatDateShort = (value?: string | null) => {
			if (!value) return "-";
			const normalized = value.trim();

			const isoPrefixMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(normalized);
			if (isoPrefixMatch) {
				const [, year, month, day] = isoPrefixMatch;
				return `${day}/${month}/${year}`;
			}

			const date = new Date(normalized);
			if (Number.isNaN(date.getTime())) return normalized;

			return new Intl.DateTimeFormat("es-VE", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			}).format(date);
		};

		const formatDateTime = (value?: string | null) => {
			if (!value) return "-";
			const date = new Date(value);
			return Number.isNaN(date.getTime())
				? value
				: new Intl.DateTimeFormat("es-VE", {
						year: "numeric",
						month: "2-digit",
						day: "2-digit",
						hour: "2-digit",
						minute: "2-digit",
						hour12: true,
					}).format(date);
		};

		const formatHour = (value?: string | null) => {
			if (!value) return "-";
			const normalized = value.trim();

			const date = new Date(normalized);
			if (!Number.isNaN(date.getTime())) {
				return new Intl.DateTimeFormat("es-VE", {
					hour: "2-digit",
					minute: "2-digit",
					hour12: true,
				}).format(date);
			}

			const timeMatch = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(normalized);
			if (!timeMatch) return normalized;

			const hour = Number(timeMatch[1]);
			const minute = Number(timeMatch[2]);
			if (
				!Number.isFinite(hour) ||
				!Number.isFinite(minute) ||
				hour < 0 ||
				hour > 23 ||
				minute < 0 ||
				minute > 59
			) {
				return normalized;
			}

			const period = hour >= 12 ? "p. m." : "a. m.";
			const hour12 = hour % 12 === 0 ? 12 : hour % 12;
			return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
		};

		const toTimestamp = (value?: string | null) => {
			if (!value) return 0;
			const timestamp = new Date(value).getTime();
			return Number.isFinite(timestamp) ? timestamp : 0;
		};

		const formatCurrency = (value?: number | null) => {
			if (typeof value !== "number" || !Number.isFinite(value)) return null;
			return `$${value.toFixed(2)}`;
		};

		const formatHoursValue = (value: number) => {
			const rounded = Math.round(value * 10) / 10;
			return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
		};

		const extractEstimatedDurations = (input: unknown): number[] => {
			if (!input) return [];

			const readDuration = (value: unknown) => {
				const parsed = Number(value);
				return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
			};

			if (Array.isArray(input)) {
				return input
					.map((item) =>
						readDuration(
							(item as { estimated_duration?: unknown })?.estimated_duration,
						),
					)
					.filter((duration): duration is number => duration !== null);
			}

			if (typeof input === "object") {
				const direct = readDuration(
					(input as { estimated_duration?: unknown })?.estimated_duration,
				);
				return direct !== null ? [direct] : [];
			}

			return [];
		};

		const formatEstimatedDuration = (
			serviceSource: unknown,
			quotationItemsSource?: unknown,
		) => {
			const serviceDurations = extractEstimatedDurations(serviceSource);
			if (serviceDurations.length > 0) {
				const total = serviceDurations.reduce((sum, value) => sum + value, 0);
				const hoursText = `${formatHoursValue(total)}h`;
				return serviceDurations.length > 1
					? `${hoursText} (${serviceDurations.length} servicios)`
					: hoursText;
			}

			const quotationItems = Array.isArray(quotationItemsSource)
				? quotationItemsSource
				: [];
			const itemDurations = quotationItems
				.map((item) =>
					Number(
						(item as { estimated_duration?: unknown })?.estimated_duration ?? 0,
					),
				)
				.filter((value) => Number.isFinite(value) && value > 0);

			if (itemDurations.length > 0) {
				const total = itemDurations.reduce((sum, value) => sum + value, 0);
				const hoursText = `${formatHoursValue(total)}h`;
				return itemDurations.length > 1
					? `${hoursText} (${itemDurations.length} servicios)`
					: hoursText;
			}

			return "-";
		};

		const formatLocation = (input: ApiAddress[]) => {
			const address = input.find((item) => item.is_active !== false) ?? input[0];
			if (!address) return { city: "-", address: "-" };
			const stateName =
				address.states?.name_state || address.states?.name_complete || null;
			const municipalityName =
				address.municipalities?.name_municipality ||
				address.municipalities?.name_complete ||
				null;
			const parishName = address.parishes?.name_parish || null;
			const countryName =
				address.states?.countries?.name_country ||
				address.states?.countries?.name_complete ||
				null;
			const street = address.street_address || null;
			const city = municipalityName || parishName || stateName || "-";
			const fullAddress =
				[street, parishName, municipalityName, stateName, countryName]
					.filter(Boolean)
					.join(", ") || "-";
			return { city, address: fullAddress };
		};

		const resolveOrderQuotation = (raw: ApiOrderRaw) => {
			if (Array.isArray(raw.quotations)) {
				if (raw.quotations.length === 0) return undefined;
				return [...raw.quotations].sort(
					(a, b) => Number(b.id ?? 0) - Number(a.id ?? 0),
				)[0];
			}
			return raw.quotations;
		};

		const mapOrderToUserOrder = (
			raw: ApiOrderRaw,
			requestsById: Map<number, ApiRequestRaw>,
		): UserOrder => {
			const provider = raw.providers;
			const quotation = resolveOrderQuotation(raw);
			const requestId = Number(raw.requests?.id ?? quotation?.requests?.id ?? 0);
			const requestFromCollection =
				requestId > 0 ? requestsById.get(requestId) : undefined;
			const request = requestFromCollection ?? raw.requests ?? quotation?.requests;
			const address = raw.addresses;
			const payments = Array.isArray(raw.payments) ? raw.payments : [];
			const hasApprovedPayment = payments.some(
				(payment) => payment.payment_status === "Aprobado",
			);
			const hasPendingPayment = payments.some(
				(payment) => payment.payment_status === "Pendiente",
			);
			const latestRejectedPayment = [...payments]
				.filter((payment) => payment.payment_status === "Rechazado")
				.sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0))[0];
			const categoryName =
				provider?.categories_providers?.[0]?.categories_services?.name_category ||
				raw.requests?.services_providers?.categories_services_providers?.[0]?.categories_providers?.categories_services?.name_category ||
				"-";
			const rawProviderName = provider?.company_name?.trim() ?? "";
			const providerName = (rawProviderName && rawProviderName.toLowerCase() !== "proveedor independiente")
				? rawProviderName
				: ([provider?.users?.names, provider?.users?.last_names].filter(Boolean).join(" ") || "-");
			const ubicacion = [
				address?.name_address,
				address?.street_address,
				address?.reference_address,
			]
				.filter(Boolean)
				.join(", ");
			const basePrice =
				request?.services_providers?.base_price ??
				quotation?.proposed_price ??
				raw.order_price_aux ??
				raw.order_price ??
				0;
			const items = (quotation?.quotation_items ?? raw.quotation_items ?? []).map(
				(qi) => ({
					description: qi.description_item || "Item",
					subtotal: qi.subtotal_item ?? 0,
				}),
			);
			const total =
				items.length > 0
					? items.reduce((sum, item) => sum + item.subtotal, 0) + basePrice
					: basePrice;
			const statusMap: Record<string, string> = {
				Dialogando: "Dialogando",
				En_Espera: "En Espera",
				En_Progreso: "En Progreso",
				Completado: "Completado",
				Soporte: "Soporte",
				Garantia: "Garantía",
				Cancelado: "Cancelado",
				Pagado: "Pagado",
			};
			const fallbackStatus =
				statusMap[raw.status_order || ""] || raw.status_order || "Desconocido";
			const displayStatus =
				latestRejectedPayment && !hasApprovedPayment && !hasPendingPayment
					? "Pago Rechazado"
					: fallbackStatus;
			const requestHour = formatHour(request?.hour_request);
			const serviceDurationSource =
				(request as { services_providers?: unknown } | undefined)
					?.services_providers ??
				raw.requests?.services_providers ??
				quotation?.requests?.services_providers;
			return {
				id: String(raw.id),
				numeroPedido: raw.code_order || `PED-${String(raw.id).padStart(3, "0")}`,
				title:
					request?.title_request ||
					quotation?.description_quotation ||
					"Servicio",
				providerName,
				description:
					request?.description_request ||
					quotation?.description_quotation ||
					"",
				fecha: formatDateTime(raw.date_initial_order),
				fechaPauta: formatDateShort(raw.estimated_date_completion),
				hora:
					requestHour !== "-"
						? requestHour
						: formatHour(raw.estimated_date_completion),
				duracionEstimada: formatEstimatedDuration(
					serviceDurationSource,
					quotation?.quotation_items ?? raw.quotation_items,
				),
				status: displayStatus,
				total: `$${total.toFixed(2)}`,
				urgency: request?.urgency_request,
				categoria: categoryName,
				subcategoria: request?.services_providers?.name_service || "-",
				ubicacion: ubicacion || "-",
				idTransaccion: raw.code_order || `PED-${String(raw.id).padStart(3, "0")}`,
				precioBase: basePrice,
				quotationItems: items,
				paymentRejectionReason:
					latestRejectedPayment?.rejection_reason_payment ?? undefined,
			};
		};

		const mapRequestToUserOrder = (raw: ApiRequestRaw): UserOrder => {
			const sp = raw.services_providers;
			const provider = sp?.providers;
			const address = raw.addresses;
			const activeQuotations = (raw.quotations ?? []).filter(
				(q) => q.status_quotation !== false,
			);
			const hasQuotations = activeQuotations.length > 0;
			let displayStatus: string;
			switch (raw.status_request) {
				case "Pendiente":
					displayStatus = hasQuotations
						? "Cotización Recibida"
						: "Solicitud Pendiente";
					break;
				case "Aceptada":
					displayStatus = "Solicitud Aceptada";
					break;
				case "Rechazada":
					displayStatus = "Solicitud Rechazada";
					break;
				case "Cancelada":
					displayStatus = "Solicitud Cancelada";
					break;
				default:
					displayStatus = "Solicitud Pendiente";
			}

			const categoryName =
				sp?.categories_services_providers?.[0]?.categories_providers
					?.categories_services?.name_category || "-";
			const rawReqProviderName = provider?.company_name?.trim() ?? "";
			const providerName = (rawReqProviderName && rawReqProviderName.toLowerCase() !== "proveedor independiente")
				? rawReqProviderName
				: (provider?.users?.names || "-");
			const ubicacion = [
				address?.name_address,
				address?.street_address,
				address?.reference_address,
			]
				.filter(Boolean)
				.join(", ");
			const latestQuotation = activeQuotations[0];
			const basePrice = latestQuotation?.proposed_price ?? sp?.base_price ?? 0;
			const items = (latestQuotation?.quotation_items ?? []).map((qi) => ({
				description: qi.description_item || "Item",
				subtotal: qi.subtotal_item ?? 0,
			}));
			const total =
				items.length > 0
					? items.reduce((sum, item) => sum + item.subtotal, 0) + basePrice
					: basePrice;
			const budgetAmount =
				typeof raw.maximun_budget === "number"
					? raw.maximun_budget
					: typeof raw.minimun_budget === "number"
						? raw.minimun_budget
						: null;
			const budget = formatCurrency(budgetAmount ?? undefined) ?? undefined;
			const requestServiceSource =
				(raw as { services_providers?: unknown }).services_providers ??
				raw.services_providers;

			const isResolved =
				raw.status_request === "Aceptada" || raw.status_request === "Rechazada";

			return {
				id: `req-${raw.id}`,
				numeroPedido: `SOL-${String(raw.id).padStart(3, "0")}`,
				title: raw.title_request || "Solicitud de Servicio",
				providerName,
				description: raw.description_request || "",
				fecha: formatDateTime(raw.created_at),
				fechaPauta: formatDateShort(raw.date_request),
				hora: formatHour(raw.hour_request),
				duracionEstimada: formatEstimatedDuration(
					requestServiceSource,
					latestQuotation?.quotation_items,
				),
				status: displayStatus,
				total: total > 0 ? `$${total.toFixed(2)}` : budget || "$0.00",
				urgency: raw.urgency_request,
				categoria: categoryName,
				subcategoria: sp?.name_service || "-",
				ubicacion: ubicacion || "-",
				idTransaccion: `SOL-${String(raw.id).padStart(3, "0")}`,
				precioBase: basePrice,
				quotationItems: items,
				isRequest: true,
				requestId: raw.id,
				quotationId: latestQuotation?.id,
				budget,
				fechaRespuesta: isResolved ? formatDateTime(raw.updated_at) : undefined,
			};
		};

		const loadProfile = async () => {
			if (!session?.user?.id || session?.user?.type_user !== "Cliente") {
				setIsLoading(false);
				return;
			}

			setIsLoading(true);
			setError(null);
			try {
				const [userPayload, addressPayload] = await Promise.all([
					fetchWithApi<UserResponse>(`/users/${session.user.id}`),
					fetchWithApi<AddressesResponse | ApiAddress[]>(
						`/addresses/client/${session.user.id}`,
					).catch(() => ({ addresses: [] }) as AddressesResponse),
				]);

				if (!userPayload) throw new Error("Usuario no encontrado");

				const user = resolveUserFromPayload(userPayload);
				if (!user) throw new Error("Usuario no encontrado");
				const resolvedUserType =
					typeof user.type_user === "string"
						? user.type_user.trim().toLowerCase()
						: "";
				if (resolvedUserType && resolvedUserType !== "cliente") {
					throw new Error(
						"La sesión actual no corresponde a un cliente. Cierra sesión e inicia con una cuenta cliente.",
					);
				}

				const fullName =
					[user.names, user.last_names].filter(Boolean).join(" ") || "Usuario";
				const since = formatDate(user.created_at || null);
				const addressesFromUser = extractAddressesFromUserPayload(userPayload);
				const parsedAddresses =
					addressesFromUser.length > 0
						? addressesFromUser
						: extractAddresses(addressPayload);
				const location = formatLocation(parsedAddresses);
				const userAddress =
					user.addresses_user || user.adresses_user || location.address;

				const payloadRecord = userPayload as Record<string, unknown>;
				const userRecord = user as Record<string, unknown>;
				const clientId =
					extractClientIdFromRelation(userRecord.clients) ??
					extractClientIdFromRelation(payloadRecord.clients) ??
					extractClientIdFromRelation(
						(payloadRecord.user as Record<string, unknown> | undefined)
							?.clients,
					);

				let allItems: UserOrder[] = [];
				if (clientId) {
					const [ordersPayload, requestsPayload] = await Promise.all([
						fetchWithApi<Record<string, unknown>>(
							`/orders/client/${clientId}?page=1&pageSize=50`,
						).catch(() => ({}) as Record<string, unknown>),
						fetchWithApi<Record<string, unknown>>(
							`/requests/client/${clientId}?page=1&pageSize=50`,
						).catch(() => ({}) as Record<string, unknown>),
					]);

					const rawOrders = ((ordersPayload as Record<string, unknown>)
						?.items ??
						(ordersPayload as Record<string, unknown>)?.orders ??
						[]) as Array<ApiOrderRaw>;
					const rawRequests = ((requestsPayload as Record<string, unknown>)
						?.items ??
						(requestsPayload as Record<string, unknown>)?.requests ??
						[]) as Array<ApiRequestRaw>;
					const requestsById = new Map(
						rawRequests.map((request) => [request.id, request] as const),
					);

					const mappedOrders = rawOrders.map((rawOrder) => ({
						item: mapOrderToUserOrder(rawOrder, requestsById),
						timestamp: toTimestamp(rawOrder.date_initial_order),
						fallbackId: Number(rawOrder.id ?? 0),
					}));
					const orderRequestIds = new Set(
						rawOrders
							.map((order) => {
								const quotation = resolveOrderQuotation(order);
								const requestId =
									order.requests?.id ?? quotation?.requests?.id;
								return typeof requestId === "number" ? requestId : null;
							})
							.filter((id): id is number => id != null),
					);
					const pendingRequests = rawRequests.filter(
						(request) => !orderRequestIds.has(request.id),
					);
					const mappedRequests = pendingRequests.map((rawRequest) => ({
						item: mapRequestToUserOrder(rawRequest),
						timestamp: toTimestamp(rawRequest.created_at),
						fallbackId: Number(rawRequest.id ?? 0),
					}));
					allItems = [...mappedRequests, ...mappedOrders]
						.sort((a, b) => {
							if (b.timestamp !== a.timestamp) {
								return b.timestamp - a.timestamp;
							}
							return b.fallbackId - a.fallbackId;
						})
						.map((entry) => entry.item);
				}

				if (!cancelled) {
					setProfile({
						name: fullName,
						email: user.email || "-",
						since,
						avatar: getImageUri(user.image_profile) || fullName.charAt(0).toUpperCase(),
						phone: user.phone || "-",
						city: location.city,
						address: userAddress || "-",
						clients: clientId ? { id: clientId } : undefined,
						dateOfBirth: user.date_of_birth ?? null,
						gender: user.gender_user ?? null,
					});
					setAddresses(parsedAddresses);
					setOrders(allItems);
				}
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof Error
							? err.message
							: "No se pudo cargar el perfil",
					);
					setProfile(null);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		void loadProfile();
		return () => {
			cancelled = true;
		};
	}, [fetchWithApi, session?.user?.id]);

	return {
		profile,
		addresses,
		orders,
		isLoading,
		error,
		refreshAddresses,
	};
}
